import React, { useEffect, useMemo, useRef, useState } from "react";
import { Box, Paper, Tab, Tabs, Typography, type SxProps } from "@mui/material";
import {
  DataGridPro,
  gridClasses,
  type GridColDef,
} from "@mui/x-data-grid-pro";
import { useStore, type Store } from "@tanstack/react-store";
import { useMutationState } from "@tanstack/react-query";
import { useAtomValue } from "jotai";
import { useParams } from "react-router-dom";
import { buildLogsAtomWithId } from "@/atoms";
import DetailModal from "../../../routes/recording/detailModal";
import {
  appetizeNetworkLogSchema,
  type AppetizeNetworkLog,
  type AppetizeCloneNetworkLog,
} from "@/utils/networkLogSchemas";
import SyntaxHighlighter from "react-syntax-highlighter";
import { stackoverflowDark } from "react-syntax-highlighter/dist/esm/styles/hljs";
import styled from "@emotion/styled";
import { DateTime } from "luxon";
import { useHotkey } from "@tanstack/react-hotkeys";
import type { MutableRefObject } from "react";

export type AnyNetworkLog = AppetizeNetworkLog | AppetizeCloneNetworkLog;

export function isAppetizeNetworkLog(
  log: AnyNetworkLog
): log is AppetizeNetworkLog {
  return appetizeNetworkLogSchema.safeParse(log).success;
}

// ─── NetworkLogDetailTabs helpers ────────────────────────────────────────────

const TabPanel = styled.div`
  padding: 16px 0;
  overflow: auto;
  height: 60vh;
`;

const DetailSection = styled.div`
  margin-bottom: 16px;
`;

const DetailSectionTitle = styled.div`
  font-weight: 600;
  font-size: 13px;
  color: #90caf9;
  margin-bottom: 8px;
  padding-bottom: 4px;
  border-bottom: 1px solid #333;
`;

const headerColumns: GridColDef[] = [
  { field: "name", headerName: "Name", flex: 1, minWidth: 150 },
  { field: "value", headerName: "Value", flex: 2, minWidth: 200 },
];

const httpHeaderColumns: GridColDef[] = [
  { field: "name", headerName: "Name", flex: 1, minWidth: 150 },
  {
    field: "value",
    headerName: "Value",
    flex: 2,
    minWidth: 200,
    renderCell: (params) => (
      <Box
        sx={{
          overflowX: "auto",
          whiteSpace: "nowrap",
          width: "100%",
          "&::-webkit-scrollbar": { height: 4 },
          "&::-webkit-scrollbar-thumb": {
            backgroundColor: "#555",
            borderRadius: 2,
          },
        }}
      >
        {params.value}
      </Box>
    ),
  },
];

const BINARY_MIME_PREFIXES = [
  "application/octet-stream",
  "application/pdf",
  "application/zip",
  "application/gzip",
  "application/x-tar",
  "application/x-protobuf",
  "application/protobuf",
  "image/",
  "audio/",
  "video/",
  "font/",
];

function isBinaryContentType(contentType: string | undefined): boolean {
  if (!contentType) return false;
  const lower = contentType.toLowerCase();
  return BINARY_MIME_PREFIXES.some((prefix) => lower.includes(prefix));
}

function renderBody(body: unknown, contentType?: string): React.ReactNode {
  if (!body) {
    return (
      <DetailSectionTitle style={{ color: "#888", fontWeight: 400 }}>
        No body
      </DetailSectionTitle>
    );
  }
  if (isBinaryContentType(contentType)) {
    return (
      <DetailSectionTitle style={{ color: "#888", fontWeight: 400 }}>
        Binary data — cannot be displayed
      </DetailSectionTitle>
    );
  }
  let parsed: unknown;
  try {
    parsed = typeof body === "string" ? JSON.parse(body) : body;
  } catch {
    parsed = body;
  }
  const content =
    typeof parsed === "string" ? parsed : JSON.stringify(parsed, null, 2);
  const language = typeof parsed === "object" ? "json" : "text";
  return (
    <SyntaxHighlighter language={language} style={stackoverflowDark}>
      {content}
    </SyntaxHighlighter>
  );
}

const httpHeaderGridSx = {
  border: "none",
  "& .MuiDataGrid-cell": { fontFamily: "monospace", fontSize: 12 },
  "& .MuiDataGrid-columnHeaders": { display: "none" },
  "& .MuiDataGrid-cell[data-field='value']": { overflowInline: "auto" },
};

export function NetworkLogDetailTabs({ log }: { log: AnyNetworkLog }) {
  const [activeTab, setActiveTab] = useState(0);

  useEffect(() => {
    setActiveTab(0);
  }, [log]);

  const isAppetize = isAppetizeNetworkLog(log);

  const generalInfoRows = useMemo(() => {
    if (isAppetize) {
      const rows = [
        { id: 0, name: "Request URL", value: log.request?.url || "-" },
        { id: 1, name: "Request Method", value: log.request?.method || "-" },
        {
          id: 2,
          name: "Status Code",
          value: String(log.response?.status ?? "-"),
        },
        {
          id: 3,
          name: "Timestamp",
          value: DateTime.fromMillis(log.absoluteTimestamp).toFormat(
            "yyyy-MM-dd hh:mm:ss.SSS"
          ),
        },
      ];
      if (log.error) {
        rows.push({ id: 4, name: "Error", value: log.error.errorText });
      }
      return rows;
    }
    const url =
      log.host && log.path
        ? `${log.scheme || "https"}://${log.host}${log.path}`
        : "-";
    const rows = [
      { id: 0, name: "Request URL", value: url },
      { id: 1, name: "Request Method", value: log.method || "-" },
      { id: 2, name: "Status Code", value: String(log.status_code ?? "-") },
    ];
    if (log.error) {
      rows.push({ id: 3, name: "Error", value: log.error });
    }
    return rows;
  }, [log, isAppetize]);

  const requestHeaderRows = useMemo(() => {
    if (isAppetize) {
      return (log.request?.headers ?? []).map((h, i) => ({
        id: i,
        name: h.name,
        value: h.value,
      }));
    }
    return Object.entries(log.request_headers ?? {}).map(
      ([name, value], i) => ({ id: i, name, value })
    );
  }, [log, isAppetize]);

  const responseHeaderRows = useMemo(() => {
    if (isAppetize) {
      return (log.response?.headers ?? []).map((h, i) => ({
        id: i,
        name: h.name,
        value: h.value,
      }));
    }
    return Object.entries(log.response_headers ?? {}).map(
      ([name, value], i) => ({ id: i, name, value })
    );
  }, [log, isAppetize]);

  const requestBody = isAppetize
    ? log.request?.postData?.text
    : log.request_body;
  const responseBody = isAppetize
    ? log.response?.content?.text
    : log.response_body;

  const requestContentType = isAppetize
    ? log.request?.headers?.find((h) => h.name.toLowerCase() === "content-type")
        ?.value
    : log.request_headers?.["content-type"] ??
      log.request_headers?.["Content-Type"];
  const responseContentType = isAppetize
    ? log.response?.headers?.find(
        (h) => h.name.toLowerCase() === "content-type"
      )?.value
    : log.response_headers?.["content-type"] ??
      log.response_headers?.["Content-Type"];

  const timingRows = useMemo(() => {
    if (isAppetize && log.timings) {
      return [
        { id: 0, name: "Send", value: `${log.timings.send ?? "-"} ms` },
        { id: 1, name: "Wait", value: `${log.timings.wait ?? "-"} ms` },
        { id: 2, name: "Receive", value: `${log.timings.receive ?? "-"} ms` },
        { id: 3, name: "Total", value: `${log.time ?? "-"} ms` },
      ];
    }
    if (!isAppetize && log.timestamp_start && log.timestamp_end) {
      const duration = (log.timestamp_end - log.timestamp_start) * 1000;
      return [
        {
          id: 0,
          name: "Started at",
          value: DateTime.fromSeconds(log.timestamp_start).toFormat(
            "hh:mm:ss.SSS"
          ),
        },
        {
          id: 1,
          name: "Ended at",
          value: DateTime.fromSeconds(log.timestamp_end).toFormat(
            "hh:mm:ss.SSS"
          ),
        },
        { id: 2, name: "Duration", value: `${duration.toFixed(2)} ms` },
      ];
    }
    return [];
  }, [log, isAppetize]);

  const tabs = [
    { label: "Headers", show: true },
    { label: "Request", show: requestBody != null },
    { label: "Response", show: responseBody != null },
    { label: "Timing", show: timingRows.length > 0 },
  ].filter((t) => t.show);

  const tabIndex = (label: string) => tabs.findIndex((t) => t.label === label);

  return (
    <Box sx={{ width: "100%" }}>
      <Box sx={{ borderBottom: 1, borderColor: "divider" }}>
        <Tabs
          value={activeTab}
          onChange={(_, newValue: number) => setActiveTab(newValue)}
          sx={{
            "& .MuiTab-root": {
              textTransform: "none",
              minWidth: 80,
              fontSize: 13,
            },
          }}
        >
          {tabs.map((tab) => (
            <Tab key={tab.label} label={tab.label} />
          ))}
        </Tabs>
      </Box>

      {activeTab === tabIndex("Headers") && (
        <TabPanel>
          <DetailSection>
            <DetailSectionTitle>General</DetailSectionTitle>
            <DataGridPro
              rows={generalInfoRows}
              columns={httpHeaderColumns}
              density="compact"
              hideFooter
              disableColumnMenu
              disableRowSelectionOnClick
              autoHeight
              sx={httpHeaderGridSx}
            />
          </DetailSection>
          {requestHeaderRows.length > 0 && (
            <DetailSection>
              <DetailSectionTitle>Request Headers</DetailSectionTitle>
              <DataGridPro
                rows={requestHeaderRows}
                columns={httpHeaderColumns}
                density="compact"
                hideFooter
                disableColumnMenu
                disableRowSelectionOnClick
                autoHeight
                sx={httpHeaderGridSx}
              />
            </DetailSection>
          )}
          {responseHeaderRows.length > 0 && (
            <DetailSection>
              <DetailSectionTitle>Response Headers</DetailSectionTitle>
              <DataGridPro
                rows={responseHeaderRows}
                columns={httpHeaderColumns}
                density="compact"
                hideFooter
                disableColumnMenu
                disableRowSelectionOnClick
                autoHeight
                sx={httpHeaderGridSx}
              />
            </DetailSection>
          )}
        </TabPanel>
      )}

      {activeTab === tabIndex("Request") && (
        <TabPanel>{renderBody(requestBody, requestContentType)}</TabPanel>
      )}

      {activeTab === tabIndex("Response") && (
        <TabPanel>{renderBody(responseBody, responseContentType)}</TabPanel>
      )}

      {activeTab === tabIndex("Timing") && (
        <TabPanel>
          <DataGridPro
            rows={timingRows}
            columns={headerColumns}
            density="compact"
            hideFooter
            disableColumnMenu
            disableRowSelectionOnClick
            autoHeight
            sx={httpHeaderGridSx}
          />
        </TabPanel>
      )}
    </Box>
  );
}

// ─── NetworkLogsTable (shared) ────────────────────────────────────────────────

const getStatusColor = (status: number | null | undefined): string => {
  if (status == null) return "#d4d4d4";
  if (status >= 200 && status < 300) return "#4caf50";
  if (status >= 300 && status < 400) return "#ff9800";
  if (status >= 400) return "#f44336";
  return "#d4d4d4";
};

export interface NetworkLogsTableProps {
  logs: any[];
  /** When provided, enables playback-sync row highlighting */
  playbackTimestamp?: number;
  currentTimestampIndex?: number;
  apiRef?: MutableRefObject<any>;
  /** Overrides the outer container Box sx. Defaults to a fixed calc(100vh-300px) height. */
  containerSx?: SxProps;
  /**
   * When true, registers a global Mod+F hotkey that focuses the DataGrid
   * quick filter (overriding the browser's native Find). Defaults to true.
   */
  enableQuickFilterHotkey?: boolean;
}

export function NetworkLogsTable({
  logs,
  playbackTimestamp,
  currentTimestampIndex,
  apiRef,
  containerSx,
  enableQuickFilterHotkey = true,
}: NetworkLogsTableProps) {
  const [selectedLog, setSelectedLog] = useState<any>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const isPlaybackMode =
    playbackTimestamp !== undefined && currentTimestampIndex !== undefined;

  const rows = useMemo(
    () =>
      logs.map((log, index) => ({
        id: index,
        time: log?.timestamp_start
          ? DateTime.fromSeconds(log.timestamp_start).toFormat("hh:mm:ss")
          : log?.absoluteTimestamp
          ? DateTime.fromMillis(log.absoluteTimestamp).toFormat("hh:mm:ss")
          : "--:--:--",
        method: log?.method || log?.request?.method || "-",
        status: log?.status_code ?? log?.response?.status ?? null,
        url:
          log?.host && log?.path
            ? `${log.scheme || "https"}://${log.host}${log.path}`
            : log?.request?.url || "-",
        relativeTimestamp: log?.relativeTimestamp ?? null,
        _original: log,
      })),
    [logs]
  );

  const columns: GridColDef[] = useMemo(
    () => [
      { field: "time", headerName: "Time", width: 90, sortable: false },
      { field: "method", headerName: "Method", width: 80, sortable: false },
      {
        field: "status",
        headerName: "Code",
        width: 70,
        sortable: false,
        renderCell: (params) => (
          <span style={{ color: getStatusColor(params.value) }}>
            {params.value ?? "-"}
          </span>
        ),
      },
      {
        field: "url",
        headerName: "Request URL",
        flex: 1,
        minWidth: 200,
        sortable: false,
      },
    ],
    []
  );

  useHotkey(
    "Mod+F",
    () => {
      const container = containerRef.current;
      if (!container) return;

      const input = container.querySelector(
        '[class*="MuiDataGrid-toolbarQuickFilter"] input'
      ) as HTMLElement;

      if (document.activeElement === input) return;

      (
        container.querySelector(
          '[class*="MuiDataGrid-toolbarQuickFilterTrigger"]'
        ) as HTMLElement
      )?.click();
    },
    { preventDefault: enableQuickFilterHotkey, enabled: enableQuickFilterHotkey }
  );

  if (logs.length === 0) {
    return (
      <Paper sx={{ p: 2 }}>
        <Typography color="text.secondary" align="center">
          No network logs yet. Logs will appear here when network requests are
          captured.
        </Typography>
      </Paper>
    );
  }

  return (
    <>
      <DetailModal
        open={!!selectedLog}
        handleClose={() => {
          setSelectedLog(null);
        }}
        title="Network Log Details"
      >
        {selectedLog && <NetworkLogDetailTabs log={selectedLog} />}
      </DetailModal>
      <Box
        ref={containerRef}
        sx={
          containerSx ?? {
            height: "calc(100vh - 300px)",
            minHeight: 400,
            width: "100%",
          }
        }
      >
        <DataGridPro
          apiRef={apiRef}
          rows={rows}
          columns={columns}
          density="compact"
          hideFooter
          showToolbar={true}
          disableColumnFilter
          disableColumnSelector
          disableDensitySelector
          disableColumnMenu
          slotProps={{
            toolbar: {
              csvOptions: { disableToolbarButton: true },
              printOptions: { disableToolbarButton: true },
            },
          }}
          disableRowSelectionOnClick
          onRowClick={(params) => {
            setSelectedLog(params.row._original);
          }}
          getRowClassName={(params) => {
            if (isPlaybackMode) {
              const { relativeTimestamp } = params.row;
              if (relativeTimestamp == null) return "";
              if (params.row.id === currentTimestampIndex) return "row-current";
              if (relativeTimestamp <= playbackTimestamp) return "row-before";
              return "row-after";
            }
            const status = params.row.status;
            if (status == null) return "";
            if (status >= 400) return "row-error";
            if (status >= 300) return "row-warning";
            return "";
          }}
          sx={{
            height: "100%",
            backgroundColor: "#1e1e1e",
            border: "none",
            borderRadius: 1,
            "& .MuiDataGrid-cell": {
              fontFamily: "monospace",
              fontSize: 12,
              borderBottom: "1px solid #333",
              cursor: "pointer",
            },
            "& .MuiDataGrid-columnHeaders": {
              backgroundColor: "#1e1e1e",
              borderBottom: "1px solid #333",
              "& .MuiDataGrid-columnHeaderTitle": {
                fontWeight: 600,
                fontSize: 12,
                color: "#90caf9",
                textTransform: "uppercase",
              },
            },
            "& .MuiDataGrid-row:hover": {
              backgroundColor: "rgba(255, 255, 255, 0.05)",
            },
            "& .row-error": { color: "#f44336" },
            "& .row-warning": { color: "#ff9800" },
            "& .row-current": {
              borderBottom: "3px solid #90caf9 !important",
            },
            "& .MuiDataGrid-virtualScroller": {
              "&::-webkit-scrollbar": { width: 8, height: 8 },
              "&::-webkit-scrollbar-thumb": {
                backgroundColor: "#555",
                borderRadius: 4,
              },
            },
            [`& .${gridClasses.columnHeader}, & .${gridClasses.cell}`]: {
              outline: "transparent",
            },
            [`& .${gridClasses.columnHeader}:focus-within, & .${gridClasses.cell}:focus-within`]:
              {
                outline: "none",
              },
            [`& .${gridClasses.row}:hover`]: { cursor: "pointer" },
          }}
        />
      </Box>
    </>
  );
}

function NetworkLogsContent({
  networkLogsStore,
}: {
  networkLogsStore: Store<any[]>;
}) {
  const logs = useStore(networkLogsStore, (state) => state);
  return <NetworkLogsTable logs={logs} />;
}

export default function NetworkLogs() {
  const { buildId } = useParams();
  const buildLogs = useAtomValue(buildLogsAtomWithId(buildId));

  const mutationData = useMutationState({
    filters: { mutationKey: ["createSessionMutation"], status: "success" },
    select: (mutation) => mutation.state.data,
  });

  const sessionData = mutationData.at(-1);
  const networkLogsStore = (sessionData as any)?.getNetworkLogsStore?.();

  if (buildLogs.networkLogs.length > 0) {
    return <NetworkLogsTable logs={buildLogs.networkLogs} />;
  }

  if (networkLogsStore) {
    return <NetworkLogsContent networkLogsStore={networkLogsStore} />;
  }

  return (
    <Paper sx={{ p: 2 }}>
      <Typography color="text.secondary" align="center">
        No network logs yet. Logs will appear here when network requests are
        captured.
      </Typography>
    </Paper>
  );
}
